import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapDrawerPage } from '../../components/CareMapDrawerPage';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { CareMapDrawerState } from '../../caremap/CareMapDrawerState';
import { FileUploader } from '../generic-notes/attachments/FileUploader';
import { useSubscriptionV2 } from '../../support/state/useSubscriptionV2';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { IonButton } from '@ionic/react';
import { AlertPresenter } from '../../AlertBinder';
import { ActiveCaremapNoteEditor } from './ActiveCaremapNoteEditor';
import { CareMapState } from '../../caremap/CareMapState';

export const EditNoteUsingCareMapDrawer: React.FC<{noteId: string; }> = ({ noteId }) => {
    const history = useInstance(HistoryViewModel);
    const drawerState = useInstance(CareMapDrawerState);
    const uploader = useInstance(FileUploader);
    const notesFactory = useInstance(CaremapNotesModelFactory);
    const alert = useInstance(AlertPresenter);
    const careMapState = useInstance(CareMapState);

    useSubscription(() => drawerState);
    useSubscription(() => uploader);

    const noteVM = useSubscriptionV2(async () => notesFactory.createEditForm(noteId));

    if(!noteVM){
        return null;
    }

    const onDidEnter = () => {
        drawerState.setIsMaskingFocusedCareMap(true);
    };

    const onWillLeave = () => {
        drawerState.setIsMaskingFocusedCareMap(false);
    };

    const saveAndClose = async () => {
        try {
            await noteVM.save();
            history.back();
        }
        catch (err: any){
            alert.showAndLogError(err);
        }
    };

    const canEdit = careMapState.hasPermissionTo('put-note');

    return (
        <CareMapDrawerPage
            stretchHeight
            title={!canEdit ? 'View Note' : 'Edit Note'}
            hasBack={!canEdit}
            hasClose={false}
            enteredHandler={onDidEnter}
            leavingHandler={onWillLeave}
            data-type="note-editor"
            buttons={canEdit && (
                <>
                    <IonButton
                        data-type="save-button"
                        disabled={!noteVM.canSave()}
                        onClick={saveAndClose}>Save</IonButton>
                    <IonButton data-type="cancel-button" fill="outline" onClick={() => {
                        history.back();
                    }}>Cancel</IonButton>
                </>
            )}
        >
            <ActiveCaremapNoteEditor
                noteVM={noteVM}
                onDeleted={() => history.back()}
            />
        </CareMapDrawerPage>
    );
};
